type Callback<T> = (data?: T) => void;

type EventBus = {
  subscribe: <T>(event: string, callback: Callback<T>) => () => void;
  publish: <T>(event: string, data?: T) => void;
};

/**
 * Event bus service allows components to publish events and subscribe to them. This approach does not depend on the location of the components in the DOM tree.
 *
 * For example, we can use it to pass data from components which were rendered in the root to popups.
 *
 * Usage:
 * 1) import eventBus in your component
 * 2) add custom key in eventBusEnum.ts
 * 3) use your key to subscribe and publish events
 *
 * Subscription:
 * const unsubscribe = eventBus.subscribe(EventBusKey.MyCustomKey, () => {
 *  reaction on event
 * })
 *
 * Don't forget to unsubscribe:
 * unsubscribe()
 *
 * Publish:
 * eventBus.publish(EventBusKey.MyCustomKey, { foo: 'bar' })
 */
const createEventBus = (): EventBus => {
  let subscribers: Record<string, Callback<any>[]> = {};

  const subscribe = <T>(event: string, callback: Callback<T>) => {
    subscribers[event] = subscribers[event] || [];
    subscribers[event].push(callback);

    return () => {
      subscribers[event] = subscribers[event].filter((sub) => sub !== callback);
    };
  };

  const publish = <T>(event: string, data?: T) => {
    if (subscribers[event]) {
      subscribers[event].forEach((callback) => (callback as Callback<T>)(data));
    }
  };

  return { subscribe, publish };
};

export const eventBus = createEventBus();
